import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  GetScheduleTimeFrameOptions,
  ListScheduleTimeFramesOptions,
  ListScheduleTimeFramesResponse,
  ScheduleTimeFrame,
  ScheduleTimeFrameUpdatedEnvelope,
  getScheduleTimeFrame as universalGetScheduleTimeFrame,
  listScheduleTimeFrames as universalListScheduleTimeFrames,
} from './calendar-v3-schedule-time-frame-schedule-time-frames.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/calendar' };

export function getScheduleTimeFrame(
  httpClient: HttpClient
): GetScheduleTimeFrameSignature {
  return (_id: string, options?: GetScheduleTimeFrameOptions) =>
    universalGetScheduleTimeFrame(
      _id,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface GetScheduleTimeFrameSignature {
  /**
   * Retrieves a schedule time frame by schedule ID.
   * @param - [Schedule](https://dev.wix.com/docs/sdk/backend-modules/calendar/schedules/introduction)
   * ID for which to retrieve the schedule time frame.
   * @returns Retrieved schedule time frame.
   */
  (_id: string, options?: GetScheduleTimeFrameOptions): Promise<
    NonNullablePaths<ScheduleTimeFrame, `status`, 2>
  >;
}

export function listScheduleTimeFrames(
  httpClient: HttpClient
): ListScheduleTimeFramesSignature {
  return (ids: string[], options?: ListScheduleTimeFramesOptions) =>
    universalListScheduleTimeFrames(
      ids,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListScheduleTimeFramesSignature {
  /**
   * Retrieves a list of schedule time frames by schedule IDs.
   *
   *
   * Sorted by schedule ID in ascending order.
   * @param - IDs of the schedules for which to retrieve schedule time frames.
   *
   * Min: 1 schedule ID
   * Max: 100 schedule IDs
   */
  (ids: string[], options?: ListScheduleTimeFramesOptions): Promise<
    NonNullablePaths<
      ListScheduleTimeFramesResponse,
      `scheduleTimeFrames` | `scheduleTimeFrames.${number}.status`,
      4
    >
  >;
}

export const onScheduleTimeFrameUpdated = EventDefinition(
  'wix.calendar.v3.schedule_time_frame_updated',
  true,
  (event: ScheduleTimeFrameUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.firstEventStartDate.utcDate' },
            { path: 'entity.lastEventEndDate.utcDate' },
            { path: 'entity.adjustedFirstEventStartDate.utcDate' },
            { path: 'entity.adjustedLastEventEndDate.utcDate' },
            { path: 'metadata.eventTime' },
            { path: 'modifiedFields.firstEventStartDate.utcDate' },
            { path: 'modifiedFields.lastEventEndDate.utcDate' },
            { path: 'modifiedFields.adjustedFirstEventStartDate.utcDate' },
            { path: 'modifiedFields.adjustedLastEventEndDate.utcDate' },
          ],
        },
      ])
    )
)<ScheduleTimeFrameUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  Address,
  AddressHint,
  BaseEventMetadata,
  BusinessSchedule,
  Categories,
  ChangeContext,
  ChangeContextPayloadOneOf,
  ConsentPolicy,
  DayOfWeek,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  GeoCoordinates,
  GetScheduleTimeFrameOptions,
  GetScheduleTimeFrameRequest,
  GetScheduleTimeFrameResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  ListScheduleTimeFramesOptions,
  ListScheduleTimeFramesRequest,
  ListScheduleTimeFramesResponse,
  Locale,
  MessageEnvelope,
  Multilingual,
  PlacementType,
  Properties,
  PropertiesChange,
  ResolutionMethod,
  RestoreInfo,
  ScheduleTimeFrame,
  ScheduleTimeFrameUpdatedEnvelope,
  SiteCloned,
  SiteCreated,
  SitePropertiesEvent,
  SitePropertiesNotification,
  SpecialHourPeriod,
  Status,
  SupportedLanguage,
  TimePeriod,
  Translation,
  WebhookIdentityType,
  ZonedDate,
} from './calendar-v3-schedule-time-frame-schedule-time-frames.universal.js';
